import { BRAND_COLORS } from "@/Utils/brandColors";
import { useEffect, useState, useMemo } from "react";
import { useCustomNavigation } from "@/hooks/useCustomNavigation";
import { AnimatedFigureIcon } from "@/Components/AvatarLifeCycle";
import { ScanUploader, UploadEvents } from "@/services/ScanUploader";
import { startNewJourney } from "@/telemetry/ids";
import { logInfo, logError } from "@/Utils/logger";
import { ReactComponent as ErrorIcon } from "@/Icons/error.svg";
import {
  getAvatarSharingData,
  hasActiveSharingSession,
} from "@/Utils/avatarSharingStorage";
import { getAvatarFromLocalStorage } from "@/Utils";
import RedirectProgress from "@/Components/RedirectProgress";
import DiscardModal from "@/Components/Modal/DiscardModal";
import { watchOrphanedScanState } from "@/Utils/scanRecovery";

import "./submitScan.scss";

// Small green check used in the success status rows (matches the design spec:
// 12×12 viewBox, #00A931 stroke, rounded caps).
const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path
      d="M2.5 6.2 L5 8.6 L9.5 3.6"
      stroke={BRAND_COLORS.success}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Trailing chevron for the "Continue now" link is rendered inside RedirectProgress.

const SubmitScan = () => {
  const { navigateTo, navigateBackTo } = useCustomNavigation();
  const [loadPercentage, setLoadPercentage] = useState(0);
  const [displayRedirectCountdown, setDisplayRedirectCountdown] =
    useState(false);
  // Armed on SUCCESS for sessions that will NOT be redirected back to a store.
  // See the effect below for what it does and why.
  const [backGoesToAvatars, setBackGoesToAvatars] = useState(false);

  const scanUploader = ScanUploader.getInstance();
  const sharingData = useMemo(() => {
    // make sure the data is preserved even after it is cleared
    // from somewhere else
    return getAvatarSharingData();
  }, []);
  const isSharing = hasActiveSharingSession();
  // Org this scan is being shared to. Empty when there's no active sharing
  // session; in that case the copy/status list fall back to the neutral,
  // non-shared wording below instead of naming an org.
  const orgName = sharingData.orgName || "";
  // Only a genuinely shared scan gets the org-specific copy ("...sent to X",
  // "...take you back to X", "Measurements sent to X"). Without this a plain
  // non-shared scan showed those lines pointing at the "TrueToForm" fallback,
  // which makes no sense (nothing is shared, and there's no redirect back).
  const isSharedScan = isSharing && orgName !== "";
  // Whether the app will actually redirect the user back to the partner after
  // success. True only for connected/widget sessions that carry a redirectUrl
  // (mirrors the redirect-countdown gate below). A standalone QR/share-link
  // scan still names an org (measurements ARE sent to it), but there's no
  // widget to return to — so it must NOT promise "we'll take you back to X".
  const willRedirectBack = isSharing && !!sharingData.redirectUrl;

  const isSisleyEnv = process.env.REACT_APP_IS_SISLEY_ENV === "true";

  // Sisley "double-check your info" review (Body Scan Procedures doc). Snapshot
  // the entered info at mount — ScanModel.create clears sharing data shortly
  // after success, and a fresh scan would overwrite newAvatar. Only the fields
  // the user provided are shown; the computed body measurements the doc lists
  // (chest / sleeve / inleg) are NOT available in the PWA and are intentionally
  // omitted here — surfacing them is a deferred backend item (see
  // SISLEY_WHITELABEL.md). Inert on every other tenant.
  const sisleyReview = useMemo<{ label: string; value: string }[]>(() => {
    if (!isSisleyEnv) return [];
    const avatar = getAvatarFromLocalStorage();
    if (!avatar) return [];
    const convertHeight = localStorage.getItem("convertHeight") === "true";
    const convertWeight = localStorage.getItem("convertWeight") === "true";
    const heightDisplay = convertHeight
      ? avatar.heightCmOnes === "0.5"
        ? `${parseInt(avatar.heightCmTens) + parseFloat(avatar.heightCmOnes)}cm`
        : `${avatar.heightCmTens}cm`
      : avatar.height;
    const weightDisplay = avatar.weight
      ? convertWeight
        ? `${avatar.weightKg}kg`
        : `${avatar.weightLb}lbs`
      : "";
    const name = `${avatar.firstName || ""} ${avatar.lastName || ""}`.trim();
    const rows: { label: string; value?: string }[] = [
      { label: "Name", value: name },
      { label: "Email", value: avatar.email },
      { label: "Gender", value: avatar.gender },
      { label: "Height", value: heightDisplay },
      { label: "Age", value: avatar.age },
      { label: "Weight", value: weightDisplay },
      {
        label: "Ordering as",
        value:
          avatar.accountType === "Company" && avatar.companyName
            ? `Company — ${avatar.companyName}`
            : avatar.accountType,
      },
      {
        label: "Dominant hand",
        value: avatar.handedness ? `${avatar.handedness} handed` : "",
      },
      { label: "Waistband", value: avatar.waistband },
      { label: "Head circumference", value: avatar.headCircumference },
      { label: "Hand", value: avatar.handMeasurement },
      { label: "Finger", value: avatar.fingerMeasurement },
      { label: "Notes", value: avatar.notes },
    ];
    return rows.filter(
      (r): r is { label: string; value: string } => !!r.value && !!r.value.trim(),
    );
  }, [isSisleyEnv]);

  const handleClose = () => {
    navigateTo("/");
  };

  const [loading, setLoading] = useState(true);
  const [uploadError, setUploadError] = useState(false);
  // Discard-confirmation modal, shown from the "Cancel" button that replaces
  // "Close" while a SHARED scan is still uploading (see footer).
  const [discardOpen, setDiscardOpen] = useState(false);

  // Figure turns green (submitted) on SUCCESS or once progress reaches 100%.
  const submitted = !loading || loadPercentage === 100;

  // Neutral post-submission copy, shared by two success states so their wording
  // can't drift: a plain non-shared scan, and a standalone QR/share-link scan
  // (which names an org but has no widget to return to).
  const processedSubtitle =
    "Your avatar is now being processed. Keep track of its progress from your avatars list.";

  // Shared scans name the destination org; only sessions that will actually
  // redirect back (connected/widget) mention the return trip. A standalone
  // QR/share-link scan is shared (org named, measurements sent) but has no
  // widget to return to, so it uses the same neutral avatars-list copy as a
  // non-shared scan. Plain non-shared scans keep the pre-2.4.1 neutral wording.
  const subtitle = isSharedScan
    ? loading
      ? `This will only take a moment. Once processed, your scan will be sent to ${orgName}.`
      : willRedirectBack
        ? `Your avatar is being processed. We'll take you back to ${orgName} shortly.`
        : processedSubtitle
    : loading
      ? "Once your scans have been successfully submitted, your avatar will start processing"
      : processedSubtitle;

  useEffect(() => {
    const scanID = localStorage.getItem("scanID");
    logInfo("[SubmitScan] mounted", {
      scanID,
      scanUploaderScanId: scanUploader.getScanId(),
      isSharing,
    });

    // Mobile browsers restore the last URL on reopen — which is THIS screen
    // when the user closed the browser mid-submission. In that case the
    // uploader singleton is fresh and will never emit, so without this check
    // the screen shows "Submitting scan" forever. Reconcile the leftover
    // localStorage state against upload_processing: if the submission
    // actually completed server-side ("submitted" — the create batch landed
    // but the local reset was lost), show the success state; if the scan is
    // dead (discarded/swept/never arrived), surface the existing error
    // banner instead of an eternal spinner. A normal in-flight submission
    // (this tab's uploader owns the scanID) returns null and is untouched.
    // Watch, don't peek: the browser restores this URL on reopen, and the
    // heartbeat that proves the previous page died only goes stale a few
    // seconds later. A one-shot check on mount answers "inconclusive" for a
    // reopen inside that window and never looks again — which is exactly how
    // a kill-and-reopen after 9s stayed stuck.
    const stopScanWatch = watchOrphanedScanState("SubmitScan", (outcome) => {
      if (outcome === "submitted") {
        setLoadPercentage(100);
        setLoading(false);
      } else {
        setUploadError(true);
      }
    });

    const cleanupProgress = scanUploader.on(
      UploadEvents.PROGRESS,
      (progress) => {
        setLoadPercentage(progress);
      },
    );
    const cleanupSuccess = scanUploader.on(UploadEvents.SUCCESS, () => {
      logInfo("[SubmitScan] received UploadEvents.SUCCESS", {
        scanID: localStorage.getItem("scanID"),
      });
      // This scan attempt is over — the next one is a NEW telemetry journey.
      // Without this, two scans inside the 2h idle TTL share one journeyId and
      // an investigator reading `journeyId == X` sees two attempts as one
      // session. Minting an id that never gets used is harmless, so a duplicate
      // call (StrictMode, a remount) costs nothing.
      startNewJourney();
      setLoading(false);
      // Only redirect back to the org when there's an ACTIVE sharing session AND
      // a URL to go to. sharingData is snapshotted at mount (so it survives a
      // mid-flow clear), which means a stale redirectUrl can outlive its session
      // — without the isSharing gate that leftover URL would hijack a non-shared
      // scan and send it back to a store. With no active session the user stays
      // in-app and reaches their avatars list via the footer button instead.
      if (isSharing && sharingData.redirectUrl) {
        setDisplayRedirectCountdown(true);
      } else {
        // Everyone else stays on this screen with /new-avatar-screen still
        // sitting in the back stack, and going back there after a success
        // shows Step 1 as never-started. Point Back at the avatars list
        // instead — see the effect below.
        setBackGoesToAvatars(true);
      }
    });
    const cleanupError = scanUploader.on(UploadEvents.ERROR, (error) => {
      logError("[SubmitScan] received UploadEvents.ERROR", {
        scanID: localStorage.getItem("scanID"),
        errorMessage: error?.message,
      });
      setUploadError(true);
    });
    const cleanupDiscarded = scanUploader.on(UploadEvents.DISCARDED, () => {
      logInfo("[SubmitScan] received UploadEvents.DISCARDED", {
        scanID: localStorage.getItem("scanID"),
      });
      // Discard is the other unambiguous end of an attempt. See SUCCESS above.
      startNewJourney();
    });
    const cleanupReset = scanUploader.on(UploadEvents.RESET, () => {
      logInfo("[SubmitScan] received UploadEvents.RESET", {
        scanID: localStorage.getItem("scanID"),
      });
    });
    return () => {
      stopScanWatch();
      cleanupProgress?.();
      cleanupSuccess?.();
      cleanupError?.();
      cleanupDiscarded?.();
      cleanupReset?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // After a success, Back must not land on /new-avatar-screen. The user is
  // finished with that form, and its Step-1 check reads a scanID that the
  // success itself correctly deleted, so it renders as never-started.
  //
  // This redirects the gesture rather than navigating on the user's behalf:
  // the success screen stays put, and Back takes them to their avatars list —
  // where the scan is visible as Processing — instead of back to the form.
  //
  // Mechanics, and why it is not `navigate(-2)`: one guard entry is pushed
  // with the SAME react-router state as the current one, so RR computes
  // delta = 0 on the pop and skips its own location update (the same trick
  // NewAvatarScreen's back-guard uses). The capture-phase listener then stops
  // the event and replaces the current entry with the avatars list. Counting
  // entries backwards would be wrong: NewAvatarScreen pushes TWO guard entries
  // of its own whenever a scanID exists, so the distance from here to the
  // avatars list is not fixed.
  //
  // Replace, not push: pushing would leave this now-stale /submit-scan entry
  // one Back press behind the avatars list, and remounting it with no scanID
  // shows an eternal "Submitting scan". Replacing means Back from the avatars
  // list reaches /new-avatar-screen, which now renders "Submitted".
  useEffect(() => {
    if (!backGoesToAvatars) return;
    const rrState = window.history.state;
    const currentUrl = window.location.href;
    window.history.pushState(rrState, "", currentUrl);

    // One-shot: this exists to redirect THE back press off the success screen,
    // once. Detaching inside the handler means it cannot keep intercepting
    // later navigation even if this screen outlives its own unmount cleanup
    // (AnimatePresence keeps an exiting page mounted until its animation
    // finishes, and an animation in a backgrounded tab may not finish at all).
    const handlePopState = (e: PopStateEvent) => {
      window.removeEventListener("popstate", handlePopState, true);
      e.stopImmediatePropagation();
      navigateBackTo("/", { replace: true });
    };
    window.addEventListener("popstate", handlePopState, true);
    return () => window.removeEventListener("popstate", handlePopState, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backGoesToAvatars]);

  return (
    <div className="submit-scan">
      <div className="submit-scan__main">
        <div className="submit-scan__figure">
          <AnimatedFigureIcon
            color={BRAND_COLORS.primaryTint}
            fillPercent={loadPercentage}
            toColor={BRAND_COLORS.primary0670}
            submitted={submitted}
            frozen={uploadError}
          />
        </div>

        <h1 className="submit-scan__title">
          {loading ? "Submitting scan" : "Scan submitted!"}
        </h1>
        <p className="submit-scan__subtitle">{subtitle}</p>

        {uploadError && (
          <div className="upload-error-banner">
            <ErrorIcon width={16} height={16} />
            <span>Unable to upload to server</span>
          </div>
        )}

        {!loading && !uploadError && (
          <div className="submit-scan__status">
            <div className="submit-scan__status-row">
              <span className="submit-scan__status-icon">
                <CheckIcon />
              </span>
              <span className="submit-scan__status-label">
                Avatar queued for processing
              </span>
            </div>
            {isSharedScan && (
              <>
                <div className="submit-scan__status-divider" />
                <div className="submit-scan__status-row">
                  <span className="submit-scan__status-icon">
                    <CheckIcon />
                  </span>
                  <span className="submit-scan__status-label">
                    Measurements sent to <b>{orgName}</b>
                  </span>
                </div>
              </>
            )}
          </div>
        )}

        {/* Sisley: let the customer double-check the info being connected to
            their orders, plus the required disclosure note. Shown once the
            scan is submitted; inert on every other tenant. */}
        {isSisleyEnv && !loading && !uploadError && (
          <div className="submit-scan__sisley-review">
            {sisleyReview.length > 0 && (
              <>
                <h2 className="submit-scan__sisley-review-title">
                  Your information
                </h2>
                <dl className="submit-scan__sisley-review-list">
                  {sisleyReview.map((row) => (
                    <div
                      className="submit-scan__sisley-review-row"
                      key={row.label}
                    >
                      <dt>{row.label}</dt>
                      <dd>{row.value}</dd>
                    </div>
                  ))}
                </dl>
              </>
            )}
            <p className="submit-scan__sisley-note">
              Sisley Clothing Australia will be able to connect this information
              to any new orders being submitted by you or on your behalf.
            </p>
          </div>
        )}
      </div>

      <div className="submit-scan__footer">
        {displayRedirectCountdown ? (
          <div className="submit-scan__footer-success">
            <RedirectProgress variant="minimal" durationSeconds={4} />
          </div>
        ) : loading && isSharing ? (
          // Closed-loop share, still uploading: the ONLY way off this screen is
          // to discard (or wait for success → redirect to the store). There is
          // no passive "Close" — leaving mid-upload used to strand the user on
          // the avatars list with the scan stuck "submitting" and no redirect.
          // Cancel opens the discard warning (which discards + ends the share +
          // returns to the store), not the "cancel measurements" dialog.
          <button
            type="button"
            onClick={() => setDiscardOpen(true)}
            className="submit-scan__close"
          >
            Cancel
          </button>
        ) : (
          // Non-shared scan (any state), or a shared scan with no store to
          // return to: submitting continues in the background; offer the list.
          <button
            type="button"
            onClick={handleClose}
            className="submit-scan__close submit-scan__close--link"
          >
            View my avatars
          </button>
        )}
      </div>

      <DiscardModal
        open={discardOpen}
        handleClose={() => setDiscardOpen(false)}
      />
    </div>
  );
};

export default SubmitScan;
